import {
    ApolloError,
    isApolloError,
    useMutation,
    useQuery,
} from '@apollo/client'
import {
    Autocomplete,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
} from '@mui/material'
import { ProcessStatus, RosterType } from '__generated__/graphql'
import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AddCandidatesToRoster } from 'recruiter/graphql/rosters.gql'
import DrawerCancelSave from 'shared/components/DrawerCancelSave'
import RostersAutocomplete from 'shared/components/RostersAutocomplete/RostersAutocomplete'
import useToast from 'shared/components/Toast/useToast'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import { RosterDrawerProps } from '.'

interface AddToExistingProps extends RosterDrawerProps {
    setIsLoading: (boolean) => void
}

const AddToExisting: FC<AddToExistingProps> = ({
    selectedCandidates,
    setIsLoading,
    onClose,
}) => {
    const toast = useToast()
    const navigate = useNavigate()

    const { data: processStatuses, loading } = useQuery(GetProcessStatuses)

    const [addCandidatesToRoster, addToRosterResult] = useMutation(
        AddCandidatesToRoster
    )
    const isLoading = useMemo(() => {
        const newVal = addToRosterResult.loading
        setIsLoading(newVal)
        return newVal
    }, [addToRosterResult.loading, setIsLoading])

    const [selectedRosterId, setSelectedRosterId] = useState<string>('')
    const [selectedProcessStatus, setSelectedProcessStatus] =
        useState<string>('')
    const [overwriteExistingStatus, setOverwriteExistingStatus] =
        useState<boolean>(false)
    const [internalNote, setInternalNote] = useState<string>('')

    useEffect(() => {
        if (processStatuses?.processStatuses && selectedProcessStatus === '') {
            setSelectedProcessStatus(processStatuses?.processStatuses?.[0]?.id)
        }
    }, [processStatuses, selectedProcessStatus])

    const processStatusValue = useMemo(
        () =>
            processStatuses?.processStatuses.find(
                (status) => status.id === selectedProcessStatus
            ),
        [processStatuses, selectedProcessStatus]
    )

    const noStatus = useMemo(
        () =>
            processStatuses?.processStatuses.find(
                (status) => status.status === ProcessStatus.NoStatus
            ),
        [processStatuses]
    )

    const handleCancel = () => {
        setSelectedRosterId('')
        setSelectedProcessStatus('')
        setOverwriteExistingStatus(false)
        setInternalNote('')
        if (onClose) onClose()
    }

    const handleSave = async () => {
        try {
            const { data } = await addCandidatesToRoster({
                variables: {
                    rosterId: selectedRosterId || '',
                    candidateIds: selectedCandidates,
                    processStatusId: selectedProcessStatus,
                    overwriteExistingStatus,
                    internalNote: internalNote || undefined,
                },
            })
            if (data?.addCandidatesToRoster.success) {
                toast({
                    title: 'Success',
                    message:
                        data?.addCandidatesToRoster.message ||
                        '1 or more candidates have been added.',
                    severity: 'success',
                    action: {
                        title: 'View Search',
                        onClick: () =>
                            navigate(`/searches/${selectedRosterId}`),
                    },
                })
                onClose()
            } else {
                toast({
                    title: 'Error',
                    message:
                        data?.addCandidatesToRoster.message ||
                        'Something went wrong',
                    severity: 'error',
                })
            }
        } catch (e) {
            if (isApolloError(e as Error)) {
                toast({
                    title: 'Error',
                    message: formatGraphQLError(
                        e as ApolloError,
                        'Something went wrong.'
                    ),
                    severity: 'error',
                })
            } else {
                toast({
                    title: 'Error',
                    message: 'Something went wrong.',
                    severity: 'error',
                })
            }
        }
    }

    return (
        <Stack
            direction="column"
            justifyContent="space-between"
            sx={{
                flexGrow: 1,
            }}
        >
            <Stack component="form" sx={{ gap: getPxAsRem(24) }}>
                <RostersAutocomplete
                    disabled={isLoading}
                    label="Name"
                    allMyRosters={true}
                    rosterTypes={[RosterType.Search]}
                    onSingleSelectionChange={(newValue) => {
                        setSelectedRosterId(newValue?.id ?? '')
                    }}
                />
                <Autocomplete
                    disabled={isLoading}
                    options={processStatuses?.processStatuses || []}
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.summaryGroup || ''
                    }
                    value={processStatusValue ?? null}
                    getOptionLabel={(option) => option.statusLabel}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Candidate Process Status"
                            helperText="Candidates that exist in the roster already would not be modified unless you check the option below."
                        />
                    )}
                    onChange={(e, value) => {
                        setSelectedProcessStatus(value?.id || '')
                    }}
                    defaultValue={noStatus}
                    // defaultValue is not set when the options have not loaded yet - key triggers a rerender for the component after they have loaded
                    key={loading.toString()}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={overwriteExistingStatus}
                            onChange={(event) =>
                                setOverwriteExistingStatus(event.target.checked)
                            }
                        />
                    }
                    label="Update existing candidates' status"
                    sx={{
                        marginLeft: 0,
                    }}
                />

                <TextField
                    label="Internal Note"
                    fullWidth
                    multiline
                    minRows={3}
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    disabled={isLoading}
                    placeholder="Note will be prepended to existing internal notes"
                    sx={{ marginBottom: getPxAsRem(24) }}
                />
            </Stack>
            <DrawerCancelSave
                isEditMode={false}
                isCancelDisabled={isLoading ?? false}
                saveButtonText={`Save (${selectedCandidates.length})`}
                isSaveDisabled={
                    selectedRosterId === null ||
                    selectedProcessStatus === '' ||
                    isLoading
                }
                isLoading={isLoading}
                handleCancel={handleCancel}
                handleSave={handleSave}
            />
        </Stack>
    )
}

export default AddToExisting
